import React from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import Svg, { Circle, Polygon, Line, Text as SvgText, G } from 'react-native-svg';
import { useTheme } from '../../contexts/ThemeContext';
import { FONTS } from '../../constants/config';
import { ChartLegend, LegendItem } from './ChartLegend';
import { CHART_COLORS } from './chartStyles';

// ============================================================
// Types
// ============================================================

export interface RadarDataPoint {
  label: string;
  value: number;
  color?: string;
}

interface RadarChartProps {
  data: RadarDataPoint[];
  size?: number;
  maxValue?: number;
  levels?: number;
  showLabels?: boolean;
  showLegend?: boolean;
  showValues?: boolean;
  fillColor?: string;
  strokeColor?: string;
  fillOpacity?: number;
  dotRadius?: number;
  title?: string;
}

// ============================================================
// RadarChart Component
// ============================================================

export const RadarChart = ({
  data,
  size: propSize,
  maxValue = 100,
  levels = 5,
  showLabels = true,
  showLegend = true,
  showValues = false,
  fillColor,
  strokeColor,
  fillOpacity = 0.3,
  dotRadius = 5,
  title,
}: RadarChartProps) => {
  const { colors, isDark } = useTheme();
  const { width: screenWidth } = useWindowDimensions();

  // Responsive size
  const size = propSize || Math.min(screenWidth - 80, 300);
  const center = size / 2;
  const radius = (size - 60) / 2; // Leave space for labels

  const chartFillColor = fillColor || colors.primary;
  const chartStrokeColor = strokeColor || colors.primary;
  const gridColor = isDark ? '#4B5563' : '#D1D5DB';
  const labelColor = colors.textMuted;

  const numPoints = data.length;
  const angleStep = (Math.PI * 2) / numPoints;

  // Calculate polygon points for data
  const getPointCoordinates = (value: number, index: number) => {
    const normalizedValue = Math.min(value, maxValue) / maxValue;
    const angle = angleStep * index - Math.PI / 2; // Start from top
    const x = center + radius * normalizedValue * Math.cos(angle);
    const y = center + radius * normalizedValue * Math.sin(angle);
    return { x, y };
  };

  // Generate polygon points string
  const polygonPoints = data
    .map((point, index) => {
      const coords = getPointCoordinates(point.value, index);
      return `${coords.x},${coords.y}`;
    })
    .join(' ');

  // Generate grid levels
  const gridLevels = Array.from({ length: levels }, (_, i) => (i + 1) / levels);

  // Generate axis lines and labels
  const axisElements = data.map((point, index) => {
    const angle = angleStep * index - Math.PI / 2;
    const endX = center + radius * Math.cos(angle);
    const endY = center + radius * Math.sin(angle);
    const labelX = center + (radius + 25) * Math.cos(angle);
    const labelY = center + (radius + 25) * Math.sin(angle);

    return { angle, endX, endY, labelX, labelY, label: point.label, value: point.value };
  });

  // Create legend items
  const legendItems: LegendItem[] = data.map((point, index) => ({
    label: point.label,
    value: point.value,
    color: point.color || CHART_COLORS.radar[Object.keys(CHART_COLORS.radar)[index % 6] as keyof typeof CHART_COLORS.radar],
  }));

  return (
    <View style={styles.container}>
      {title && (
        <Text style={[styles.title, { color: colors.textPrimary }]}>{title}</Text>
      )}

      <View style={styles.chartWrapper}>
        <Svg width={size} height={size}>
          {/* Grid Circles */}
          {gridLevels.map((level, i) => {
            const levelRadius = radius * level;
            const levelPoints = data
              .map((_, index) => {
                const angle = angleStep * index - Math.PI / 2;
                const x = center + levelRadius * Math.cos(angle);
                const y = center + levelRadius * Math.sin(angle);
                return `${x},${y}`;
              })
              .join(' ');

            return (
              <Polygon
                key={`grid-${i}`}
                points={levelPoints}
                fill="none"
                stroke={gridColor}
                strokeWidth={1}
                strokeDasharray={i < levels - 1 ? '4,4' : '0'}
                opacity={0.5}
              />
            );
          })}

          {/* Axis Lines */}
          {axisElements.map((axis, index) => (
            <Line
              key={`axis-${index}`}
              x1={center}
              y1={center}
              x2={axis.endX}
              y2={axis.endY}
              stroke={gridColor}
              strokeWidth={1}
              opacity={0.5}
            />
          ))}

          {/* Data Polygon */}
          <Polygon
            points={polygonPoints}
            fill={chartFillColor}
            fillOpacity={fillOpacity}
            stroke={chartStrokeColor}
            strokeWidth={2}
          />

          {/* Data Points */}
          {data.map((point, index) => {
            const coords = getPointCoordinates(point.value, index);
            const pointColor = point.color || chartStrokeColor;
            return (
              <G key={`point-${index}`}>
                {/* Outer ring */}
                <Circle
                  cx={coords.x}
                  cy={coords.y}
                  r={dotRadius + 2}
                  fill={colors.card}
                  stroke={pointColor}
                  strokeWidth={2}
                />
                {/* Inner dot */}
                <Circle
                  cx={coords.x}
                  cy={coords.y}
                  r={dotRadius - 1}
                  fill={pointColor}
                />
              </G>
            );
          })}

          {/* Axis Labels */}
          {showLabels &&
            axisElements.map((axis, index) => (
              <SvgText
                key={`label-${index}`}
                x={axis.labelX}
                y={axis.labelY}
                fontSize={10}
                fill={labelColor}
                textAnchor="middle"
                alignmentBaseline="middle"
                fontFamily="JF Flat"
              >
                {axis.label}
              </SvgText>
            ))}

          {/* Value Labels (optional) */}
          {showValues &&
            data.map((point, index) => {
              const coords = getPointCoordinates(point.value, index);
              return (
                <SvgText
                  key={`value-${index}`}
                  x={coords.x}
                  y={coords.y - 12}
                  fontSize={9}
                  fill={colors.textPrimary}
                  textAnchor="middle"
                  fontFamily="JF Flat"
                >
                  %{point.value}
                </SvgText>
              );
            })}

          {/* Center percentage labels */}
          {gridLevels.map((level, i) => {
            if (i === 0 || i === levels - 1) {
              return (
                <SvgText
                  key={`percent-${i}`}
                  x={center + 5}
                  y={center - radius * level + 4}
                  fontSize={8}
                  fill={labelColor}
                  textAnchor="start"
                  opacity={0.7}
                >
                  %{Math.round(level * maxValue)}
                </SvgText>
              );
            }
            return null;
          })}
        </Svg>
      </View>

      {/* Legend */}
      {showLegend && <ChartLegend items={legendItems} />}
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    textAlign: 'center',
    marginBottom: 16,
  },
  chartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default RadarChart;
